import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"
import { LocalDate } from '@js-joda/core';

// Relative on purpose. Three tsconfigs compile this file and none of them
// agree on an alias: the API project maps `@trashlab/viteapp/*` but not `@/*`,
// while sitesapp pulls all of viteapp into its program with `@/*` pointing at
// its own `src`. A relative path is the only specifier correct in all three.
import { OVERLAY_LAYER_TOKENS } from "../components/ui/overlay-layers"

// Register the semantic overlay tokens with tailwind-merge. Without this it
// treats `z-dialog` as an unknown class, so `cn('z-dialog', 'z-[9999]')` would
// emit both and leave the winner to stylesheet order rather than the call site.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      z: [{ z: OVERLAY_LAYER_TOKENS }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date-only value (from PostgreSQL date columns) without timezone conversion.
 * Uses js-joda LocalDate which is inherently timezone-free.
 *
 * PostgreSQL `date` type stores just a date (no time/timezone). When read via Prisma,
 * it may come as a Date object (at UTC midnight) or as an ISO date string.
 * Using `new Date("2024-02-15")` in JS interprets this as UTC midnight, which when
 * converted to local time can shift the displayed date by 1 day.
 *
 * This function uses js-joda LocalDate to avoid timezone issues entirely.
 *
 * @param value - ISO date string or Date object from Prisma
 * @param format - 'short' (Feb 03), 'medium' (Feb 03, 2026), or 'long' (February 03, 2026)
 */
export function formatDateOnly(
  value: string | Date | null | undefined,
  format: 'short' | 'medium' | 'long' = 'medium'
): string {
  if (!value) return 'N/A';

  try {
    let localDate: LocalDate;

    if (typeof value === 'string') {
      // ISO date string like "2024-02-15" or "2024-02-15T00:00:00.000Z"
      localDate = LocalDate.parse(value.slice(0, 10));
    } else {
      // Date object - extract UTC components since Prisma stores dates at UTC midnight
      localDate = LocalDate.of(
        value.getUTCFullYear(),
        value.getUTCMonth() + 1,
        value.getUTCDate()
      );
    }

    // Use Intl.DateTimeFormat for locale-aware formatting
    // Create a date at noon to avoid any edge cases
    const dateForFormatting = new Date(localDate.year(), localDate.monthValue() - 1, localDate.dayOfMonth(), 12, 0, 0);

    switch (format) {
      case 'short':
        // "Feb 03"
        return dateForFormatting.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
      case 'long':
        // "February 03, 2026"
        return dateForFormatting.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' });
      case 'medium':
      default:
        // "Feb 03, 2026"
        return dateForFormatting.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    }
  } catch {
    return 'N/A';
  }
}

export function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, "");
  
  // Check if we have a valid 10-digit number
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  const match11 = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);

  if (match11) {
    return `(${match11[2]}) ${match11[3]}-${match11[4]}`;
  }

  // Return original if not a valid phone number
  return phoneNumber;
}
